import * as React from 'react';
import { visualize, story, snap } from 'storybook-snapper';
import {
  TabsAlignment,
  TabsSkin,
  Tabs,
  TabsVariant,
  TabsProps,
  OverflowBehavior,
} from '..';
import { TPAComponentsProvider } from '../TPAComponentsConfig';
import { FavoriteSmall, LikeHeartSmall } from '@wix/wix-ui-icons-common/on-stage';
import { classes } from './Tabs.visual.st.css';

const items = [0, 1, 2, 4].map((i) => ({ title: `Title ${i}` }));
const itemsWithPrefix = [0, 1, 2].map((i) => ({
  title: `Title ${i}`,
  prefix: <FavoriteSmall />,
}));

const itemsWithSuffix = [0, 1, 2].map((i) => ({
  title: `Title ${i}`,
  suffix: i === 1 ? <FavoriteSmall /> : undefined,
}));

const itemsWithBothAffixes = [0, 1, 2].map((i) => ({
  title: `Title ${i}`,
  prefix: <LikeHeartSmall />,
  suffix: i === 1 ? <FavoriteSmall /> : undefined,
}));

interface TabsVisualProps {
  tabsProps: TabsProps;
  mobile: boolean;
  isRtl: boolean;
  compact?: boolean;
}

class TabsVisual extends React.Component<TabsVisualProps> {
  static defaultProps = {
    tabsProps: {},
    mobile: false,
    isRtl: false,
  };

  render() {
    const { tabsProps, mobile, compact, isRtl } = this.props;
    const style = compact ? { margin: '10px', maxWidth: 200 } : undefined;

    return (
      <div dir={isRtl ? 'rtl' : 'ltr'}>
        <TPAComponentsProvider value={{ mobile, rtl: isRtl }}>
          <div style={style}>
            <Tabs {...tabsProps} />
          </div>
        </TPAComponentsProvider>
      </div>
    );
  }
}

visualize('Tabs', () => {
  const renderTest = (
    renderProps?: Omit<
      Partial<TabsVisualProps> & {
        props?: Partial<TabsProps>;
      },
      'tabsProps'
    >,
  ) => {
    const { props, ...testProps } = renderProps || {};
    return (
      <TabsVisual
        tabsProps={{
          items,
          activeTabIndex: 0,
          ...props,
        }}
        {...testProps}
      />
    );
  };

  story('basic', () => {
    snap('default', renderTest());

    snap('mobile', renderTest({ mobile: true }));

    snap('with scroll arrows', renderTest({ compact: true }));

    snap('with icon prefix', renderTest({ props: { items: itemsWithPrefix } }));

    snap('with icon suffix', renderTest({ props: { items: itemsWithSuffix } }));

    snap('with both prefix and suffix icons', renderTest({ props: { items: itemsWithBothAffixes } }));

    snap(
      'with gaps',
      renderTest({
        props: {
          className: classes.gaps,
        },
        compact: true,
      }),
    );

    snap(
      'with icon prefix RTL',
      renderTest({ props: { items: itemsWithPrefix }, isRtl: true }),
    );

    snap(
      'with suffix elements RTL',
      renderTest({ props: { items: itemsWithSuffix }, isRtl: true }),
    );

    snap(
      'with overrides',
      renderTest({ props: { className: classes.styled } }),
    );

    snap(
      'Navigation Arrows',
      renderTest({
        compact: true,
        props: {
          className: classes.tabNavBtn,
        },
      }),
    );

    snap(
      'custom tab container',
      renderTest({ props: { className: classes.customTabContainer } }),
    );
  });

  story('Alignments', () => {
    Object.values(TabsAlignment).map((alignment) => {
      snap(alignment, renderTest({ props: { alignment } }));

      snap(
        `with wrap and ${alignment} alignment`,
        renderTest({
          props: { overflowBehavior: OverflowBehavior.wrap, alignment },
          compact: true,
        }),
      );
    });
  });

  story('TabsVariants', () => {
    Object.values(TabsVariant).map((variant) => {
      snap(variant, renderTest({ props: { variant } }));

      snap(
        `with wrap and ${variant} variant`,
        renderTest({
          props: { overflowBehavior: OverflowBehavior.wrap, variant },
          compact: true,
        }),
      );
    });
  });

  story('Skins', () => {
    Object.values(TabsSkin).map((skin) => {
      snap(skin, renderTest({ props: { skin } }));

      snap(
        `with wrap and ${skin} skin`,
        renderTest({
          props: { overflowBehavior: OverflowBehavior.wrap, skin },
          compact: true,
        }),
      );
    });
  });

  story('Tab link', () => {
    const linkTabs = Array.from({ length: 3 }, (_, index) => ({
      title: `Link tab ${index + 1}`,
      href: `/some-href-${index}`,
    }));

    snap(
      'tab href',
      renderTest({
        props: { items: linkTabs },
      }),
    );
  });

  story('Tab style', () => {
    snap(
      'tab background',
      renderTest({ props: { className: classes.tabBackground } }),
    );

    snap(
      'tab border radius',
      renderTest({ props: { className: classes.tabBorderRadius } }),
    );

    snap(
      'tab borders',
      renderTest({ props: { className: classes.tabBorders } }),
    );
  });

  story('Selected tab style', () => {
    snap(
      'selected indicator with active tab',
      renderTest({
        props: {
          className: classes.selectedIndicator,
          activeTabIndex: 1
        }
      }),
    );

    snap(
      'selected indicator with background',
      renderTest({
        props: {
          className: classes.selectedIndicatorWithBackground,
          activeTabIndex: 1
        }
      }),
    );

    snap(
      'selected indicator with borders',
      renderTest({
        props: {
          className: classes.selectedIndicatorWithBorders,
          activeTabIndex: 1
        }
      }),
    );

    snap(
      'selected indicator with text styling',
      renderTest({
        props: {
          className: classes.selectedIndicatorWithText,
          activeTabIndex: 1
        }
      }),
    );

    snap(
      'selected indicator with corner radius',
      renderTest({
        props: {
          className: classes.selectedIndicatorWithRadius,
          activeTabIndex: 1
        }
      }),
    );

    snap('with fallbacks', () =>
      renderTest({
        props: {
          items,
          activeTabIndex: 1
        },
      })
    )
    })

  story('Wrap behavior', () => {
    snap(
      'basic',
      renderTest({
        props: {
          overflowBehavior: OverflowBehavior.wrap,
        },
        compact: true,
      }),
    );

    snap(
      'with gaps',
      renderTest({
        props: {
          overflowBehavior: OverflowBehavior.wrap,
          className: classes.gaps,
        },
        compact: true,
      }),
    );
  });

  story('Tabs border', () => {
    snap(
      'with basic border',
      renderTest({ props: { className: classes.basicBorder } }),
    );

    snap(
      'with colorful border',
      renderTest({ props: { className: classes.colorfulBorder } }),
    );

    snap(
      'with padding border and background',
      renderTest({ props: { className: classes.paddingBorder } }),
    );
  });

  story('Font size', () => {
    snap(
      'with default font bigger than selected font',
      renderTest({ props: { className: classes.bigDefaultFont } }),
    );

    snap(
      'with selected font bigger than default font',
      renderTest({ props: { className: classes.bigSelectedFont } }),
    );
  })

  story('With wst variables', () => {
    snap('default', () => {
      const style = {
        '--wst-primary-background-color-rgb': '170,120,120',
        '--wst-paragraph-2-color-rgb': '0,100,0',
        '--wst-paragraph-2-font-family': 'Courier New',
        '--wst-links-and-actions-color-rgb': '255,100,0',
        '--wix-ui-tpa-tabs-border-width-block-start': '2px',
        '--wix-ui-tpa-tabs-border-width-inline-start': '2px',
        '--wix-ui-tpa-tabs-border-width-inline-end': '2px',
        '--wix-ui-tpa-tabs-border-width-block-end': '2px',
      } as React.CSSProperties;

      return (
        <div style={style}>
          {renderTest({ props: { skin: TabsSkin.fullUnderline } })}
          <br />
          {renderTest({
            props: {
              skin: TabsSkin.fitUnderline,
              overflowBehavior: OverflowBehavior.scroll,
            },
            compact: true,
          })}
        </div>
      );
    });

    snap('font', () => {
      const style = {
        '--wst-paragraph-2-color-rgb': '0,100,0',
        '--wst-paragraph-2-font-family': 'Libre Baskerville',
        '--wst-paragraph-2-font-size': '22px',
        '--wst-paragraph-2-line-height': '3',
        '--wst-paragraph-2-font-weight': '900',
        '--wst-paragraph-2-font-style': 'italic',
        '--wst-paragraph-2-font-variant': 'small-caps',
      } as React.CSSProperties;

      return (
        <div style={style}>
          {renderTest({ props: { skin: TabsSkin.fullUnderline } })}
          <br />
          {renderTest({
            props: {
              skin: TabsSkin.fitUnderline,
              overflowBehavior: OverflowBehavior.scroll,
            },
            compact: true,
          })}
        </div>
      );
    });

    snap('with border', () => {
      const style = {
        '--wst-paragraph-2-color-rgb': '0,100,0',
        '--wix-ui-tpa-tabs-border-width-block-start': '2px',
        '--wix-ui-tpa-tabs-border-width-inline-start': '2px',
        '--wix-ui-tpa-tabs-border-width-inline-end': '2px',
        '--wix-ui-tpa-tabs-border-width-block-end': '2px',
      } as React.CSSProperties;

      return (
        <div style={style}>
          {renderTest({ props: { skin: TabsSkin.clear } })}
        </div>
      );
    });
    snap('wst underline', () => {
      return (
        <div>
          {renderTest({
            props: {
              className: classes.wstUnderline,
              skin: TabsSkin.fitUnderline,
              overflowBehavior: OverflowBehavior.scroll,
            },
            compact: true,
          })}
        </div>
      );
    });
  });
});
